import { Drawer, Form, InputNumber, Input, Radio, Select } from 'antd';
import { useEffect, useState } from 'react';
import { ActionValueType, DataType, Kind, TableData } from '.';
import { MiniProgramType } from '@/pages/miniProgram/types';
import Upload from '@/components/upload';
import { useDispatch, useSelector } from 'umi';
import { LinkType } from '@/pages/link/types';
import Recorder from './recorder';
import {
  ActionClass,
  ActionProp,
  ActionType,
  ReceiveActionList,
  SendActionList,
} from './node-default';
import { ChannelPayload } from '@/pages/channel/types';

const { Option } = Select;

interface ActionDrawerProps {
  setTableData?: (data: TableData) => void;
  setCommonRuleData?: (data: DataType[]) => void;
  node: string;
  tableData: TableData;
  platform: 1 | 2;
}

const Index: React.FC<ActionDrawerProps> = ({
  node,
  tableData,
  setTableData,
  setCommonRuleData,
  platform,
}) => {
  const [actionForm] = Form.useForm();

  const [actionList, setActionList] = useState<ActionType[]>([]);
  const [action, setAction] = useState<ActionProp>('say');
  const [currentEvent, setCurrentEvent] = useState<Kind>('onEnter');
  const [currentActionData, setCurrentActionData] = useState<DataType>();
  const [onActionKey, setOnActionKey] = useState<string>('');

  const miniProgramList = useSelector(
    (state: any) => state.open.miniProgramList,
  );
  const linkList = useSelector((state: any) => state.open.linkList);
  const channelList = useSelector((state: any) => state.open.channelList);
  const actionInfo = useSelector((state: any) => state.open.actionInfo);
  const dispatch = useDispatch();

  useEffect(() => {
    if (actionInfo.show) {
      if (onActionKey !== actionInfo.d.key) {
        setOnActionKey(actionInfo.d.key);
        actionForm.resetFields();
      }

      setActionList(
        ['onEnter', 'onIdle'].includes(actionInfo.kind)
          ? platform === 1
            ? SendActionList.filter((a) => !['businessCard'].includes(a.prop))
            : SendActionList.filter((a) =>
                ['say', 'image', 'businessCard'].includes(a.prop),
              )
          : ReceiveActionList,
      );
      setCurrentEvent(actionInfo.kind);
      setCurrentActionData(actionInfo.d);
      setAction(actionInfo.d.action);
      actionForm.setFieldsValue({ ...actionInfo.d });
    }
  }, [actionInfo, platform]);

  const handleActionFormChange = (changedValues: any) => {
    let { action: __action } = changedValues;
    if (__action) setAction(__action);

    if (!actionInfo.nodeId && setCommonRuleData) {
      setCommonRuleData([{ ...actionInfo.d, code: changedValues.code }]);
      return;
    }

    const data = tableData[node][currentEvent].map((d) => {
      if (d.key === currentActionData?.key) {
        if (__action) {
          d['action'] = __action;
        }
        let actionProps = [...ActionClass.getActionPropList(), 'delay'];
        for (let i = 0; i < actionProps.length; i++) {
          let prop = actionProps[i] as ActionValueType;
          if (changedValues[prop] !== undefined) {
            d[prop] = changedValues[prop];
            break;
          }
        }
      }
      return d;
    });
    const newItem = { ...tableData[node], [currentEvent]: data };
    const newData = {
      ...tableData,
      [node]: newItem,
    };
    if (setTableData) setTableData(newData);
  };

  const setImageUrl = (url: string) => {
    const oldData = actionForm.getFieldsValue();
    actionForm.setFieldsValue({ ...oldData, image: url });
    handleActionFormChange({ image: url });
  };

  const setVideoUrl = (url: string) => {
    const oldData = actionForm.getFieldsValue();
    actionForm.setFieldsValue({ ...oldData, video: url });
    handleActionFormChange({ video: url });
  };

  const setVoice = (url: string) => {
    const oldData = actionForm.getFieldsValue();
    actionForm.setFieldsValue({ ...oldData, voice: url });
    handleActionFormChange({ voice: url });
  };

  return (
    <Drawer
      title="配置"
      width={500}
      visible={actionInfo.show}
      placement="left"
      onClose={() => {
        dispatch({ type: 'open/closeActionEditDrawer' });
        actionForm.setFieldsValue({
          action: '',
          delay: '',
          ...ActionClass.initSendValue,
        });
      }}
    >
      <Form
        form={actionForm}
        onValuesChange={handleActionFormChange}
        labelCol={{ span: 4 }}
        wrapperCol={{ span: 19 }}
      >
        <Form.Item label="内容" name="action">
          <Radio.Group>
            {actionList.map((vo) => (
              <Radio.Button type="primary" key={vo.prop} value={vo.prop}>
                {vo.label}
              </Radio.Button>
            ))}
          </Radio.Group>
        </Form.Item>
        {['onEnter', 'onIdle'].includes(currentEvent) && (
          <Form.Item label="延迟(秒)" name="delay">
            <InputNumber
              placeholder="请输入延迟发送时长"
              style={{ width: 200 }}
              min={1}
            />
          </Form.Item>
        )}
        {['onReceive'].includes(currentEvent) && (
          <>
            <Form.Item label="规则" name="code">
              <Select>
                {['匹配年龄', '匹配ABCD', '匹配字段'].map((m: string) => (
                  <Option key={m} value={m}>
                    {m}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </>
        )}
        {action === 'say' && (
          <Form.Item label="文字" name="text">
            <Input.TextArea placeholder="请输入发送的文字" />
          </Form.Item>
        )}
        {action === 'createGroup' && (
          <Form.Item label="拉群" name="createGroup">
            <Input.TextArea placeholder="请输入拉群后要发送的文字" />
          </Form.Item>
        )}
        {action === 'reactivateGroup' && (
          <Form.Item label="激活群" name="reactivateGroup">
            <Input.TextArea placeholder="请输入@用户的话术" />
          </Form.Item>
        )}
        {action === 'businessCard' && (
          <Form.Item label="名片" name="businessCard">
            <Select placeholder="请选择要发送的名片">
              <Option key={'first'} value={'第一个'}>
                {'默认第一个'}
              </Option>
            </Select>
          </Form.Item>
        )}
        {action === 'miniProgram' && (
          <Form.Item label="小程序" name="miniProgram">
            <Select placeholder="请选择发送的小程序（如不存在请先新建该小程序）">
              {miniProgramList.map((m: MiniProgramType) => (
                <Option key={m.id} value={m.id}>
                  {m.appid + '-' + m.title}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}
        {action === 'channel' && (
          <Form.Item label="视频号" name="channel">
            <Select placeholder="请选择发送的视频号（如不存在请先新建该视频号）">
              {channelList.map((m: ChannelPayload) => (
                <Option key={m.id} value={m.id}>
                  {m.id + '-' + m.nickname}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}
        {action === 'link' && (
          <Form.Item label="链接" name="link">
            <Select placeholder="请选择发送的链接（如不存在请先新建该链接）">
              {linkList.map((m: LinkType) => (
                <Option key={m.id} value={m.id}>
                  {m.title}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}
        {action === 'image' && (
          <>
            <Form.Item label="图片" name="image">
              <Input placeholder="可自行上传图片或输入图片链接" />
            </Form.Item>
            <Form.Item
              label="上传图片"
              shouldUpdate={(pre, cur) => pre.image !== cur.image}
            >
              {() => (
                <Upload
                  url={actionForm.getFieldValue('image')}
                  setImageUrl={setImageUrl}
                />
              )}
            </Form.Item>
          </>
        )}
        {action === 'video' && (
          <>
            <Form.Item label="视频" name="video">
              <Input placeholder="可自行上传视频或输入视频链接" />
            </Form.Item>
            <Form.Item
              label="上传视频"
              shouldUpdate={(pre, cur) => pre.video !== cur.video}
            >
              {() => (
                <Upload
                  url={actionForm.getFieldValue('video')}
                  setImageUrl={setVideoUrl}
                  fileType="video"
                />
              )}
            </Form.Item>
          </>
        )}
        {action === 'voice' && (
          <>
            <Form.Item label="音频" name="voice">
              <Input placeholder="可自行录制音频或输入音频链接" />
            </Form.Item>
            <Form.Item label="录制音频">
              <Recorder
                voice={actionForm.getFieldValue('voice')}
                setVoice={setVoice}
              />
            </Form.Item>
          </>
        )}
      </Form>
    </Drawer>
  );
};

export default Index;
